import React, { type FunctionComponent, Fragment } from 'react'
import styled from '@emotion/styled'
import { fontSize, colors, fontWeight, LoadingDots } from '@retailer-platform/shared-components'
import { MetricCardChange } from '@retailer-platform/shared-components/src/common/metric-card/components/MetricCardChange'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'

export type MetricCardValue = number | undefined | null

export interface MetricCardProps {
  title?: string
  valueFormatFn: (value: number) => string
  value: MetricCardValue
  valueLabel?: string
  secondaryValueLabel?: string
  defaultValue?: string
  comparisonValue?: MetricCardValue
  secondaryValue?: MetricCardValue
  secondaryComparisonValue?: MetricCardValue
  desiredDirection?: 'positive' | 'negative'
  changeSuffix?: string
  secondaryChangeSuffix?: string
  'data-testid'?: string
  isLoading?: boolean
  active?: boolean
  layoutVariant?: 'square' | 'stretch'
  onClick?: () => void
  className?: string
}

const MetricContainer = styled.div(
  ({ active, onClick }: Pick<MetricCardProps, 'active' | 'onClick'>) => ({
    height: 53,
    minWidth: 170,
    fontSize: fontSize.X15,
    boxShadow: active ? '0px 2px 8px rgba(0, 0, 0, 0.16)' : 'none',
    width: '100%',
    border: `1px solid ${colors.GRAYSCALE.X20}`,
    borderRadius: '12px',
    cursor: onClick ? 'pointer' : 'default',
    backgroundColor: active ? colors.GRAYSCALE.X0 : colors.GRAYSCALE.X10,
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    padding: '0 24px',
  })
)

const ValueContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  height: '50px',
})

const Value = styled.div({
  fontSize: fontSize.X18,
  lineHeight: '26px',
  fontWeight: fontWeight.SEMIBOLD,
  marginBottom: '4px',
})

const GrowthPercentageGroupContainer = styled.div({
  display: 'flex',
})

const TitleContainer = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: fontSize.X15,
  color: colors.SECONDARY.DARK,
  lineHeight: '18px',
  justifyContent: 'center',
}))

export const InsightsStretchedMetricCard: FunctionComponent<MetricCardProps> = ({
  title,
  valueFormatFn,
  value,
  defaultValue = '--',
  comparisonValue,
  secondaryValue,
  secondaryComparisonValue,
  desiredDirection,
  changeSuffix,
  secondaryChangeSuffix,
  'data-testid': testId,
  isLoading,
  active,
  layoutVariant = 'square',
  onClick,
  ...rest
}) => {
  const displayValue = isNil(value) ? defaultValue : valueFormatFn(value)

  return (
    <MetricContainer active={active} data-testid={testId} onClick={onClick} {...rest}>
      <ValueContainer>
        {isLoading ? (
          <div css={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <LoadingDots testId="metric-card-loading" height={15} />
          </div>
        ) : (
          <>
            <Value>{displayValue}</Value>

            <GrowthPercentageGroupContainer>
              <div style={{ marginBottom: -8 }}>
                <MetricCardChange
                  value={value}
                  comparisonValue={comparisonValue}
                  changeSuffix={changeSuffix}
                  desiredDirection={desiredDirection}
                />
              </div>
              {isNumber(secondaryValue) && isNumber(secondaryComparisonValue) && (
                <Fragment>
                  /
                  <MetricCardChange
                    value={secondaryValue}
                    comparisonValue={secondaryComparisonValue}
                    changeSuffix={secondaryChangeSuffix || changeSuffix}
                    desiredDirection={desiredDirection}
                  />
                </Fragment>
              )}
            </GrowthPercentageGroupContainer>
          </>
        )}
      </ValueContainer>
      <TitleContainer>{<div>{title}</div>}</TitleContainer>
    </MetricContainer>
  )
}
